import { useEffect, useState } from "react";
import axios from "../../api/axios";
import SidebarAdmin from "../../components/SidebarAdmin";
import { formatLocalDate, formatLocalDateTime, parseServerDate } from "../../utils/dateTime";

export default function Billing() {
  const [summary, setSummary] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [renewalRequests, setRenewalRequests] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState("transactions");
  const [showModal, setShowModal] = useState(false);
  const [newTx, setNewTx] = useState({ user_id: "", plan_id: "", amount: "", payment_method: "UPI", status: "COMPLETED", notes: "" });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [sumRes, txRes, usersRes, plansRes, rrRes, invRes] = await Promise.all([
        axios.get("/admin/billing-summary"),
        axios.get("/admin/transactions"),
        axios.get("/admin/users"),
        axios.get("/admin/plans"),
        axios.get("/admin/plan-renewal-requests?status=PENDING"),
        axios.get("/admin/invoices?limit=100"),
      ]);
      setSummary(sumRes.data);
      setTransactions(txRes.data);
      setUsers(usersRes.data);
      setPlans(plansRes.data);
      setRenewalRequests(Array.isArray(rrRes.data) ? rrRes.data : []);
      setInvoices(Array.isArray(invRes.data) ? invRes.data : []);
    } catch (err) {
      console.error("Failed to fetch billing data:", err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    try {
      await axios.post("/admin/transactions", newTx);
      setShowModal(false);
      setNewTx({ user_id: "", plan_id: "", amount: "", payment_method: "UPI", status: "COMPLETED", notes: "" });
      fetchData();
    } catch (err) {
      alert("Failed to add transaction");
    }
  };

  const approveRenewal = async (id, method) => {
    try {
      const res = await axios.post(`/admin/plan-renewal-requests/${id}/approve`, {
        payment_method: method,
      });

      const emailSent = Boolean(res.data?.emailSent);
      const emailErr = res.data?.emailError;

      if (emailSent) {
        alert("Renewal approved and invoice PDF sent on Email");
      } else {
        alert(`Renewal approved, but Email send failed: ${emailErr || "unknown error"}`);
      }

      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to approve";
      alert(msg);
    }
  };

  const rejectRenewal = async (id) => {
    const notes = window.prompt("Reject reason (optional):") || "";
    try {
      await axios.post(`/admin/plan-renewal-requests/${id}/reject`, {
        admin_notes: notes,
      });
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to reject";
      alert(msg);
    }
  };

  const resendInvoice = async (id) => {
    try {
      await axios.post(`/admin/invoices/${id}/resend-email`);
      alert("Invoice sent on Email");
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to resend";
      alert(msg);
    }
  };

  const summaryCards = [
    { label: "Total Revenue", value: `₹${summary.total_revenue || 0}`, icon: "💰", color: "border-green-500" },
    { label: "This Month", value: `₹${summary.monthly_revenue || 0}`, icon: "📅", color: "border-blue-500" },
    { label: "Pending", value: `₹${summary.pending_amount || 0}`, icon: "⏳", color: "border-yellow-500" },
    { label: "Expired Users", value: summary.expiredUsers || 0, icon: "⚠️", color: "border-red-500" },
  ];

  const planById = plans.reduce((acc, p) => {
    acc[String(p.id)] = p;
    return acc;
  }, {});

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarAdmin />
      <div className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 mt-14 lg:mt-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">Billing & Payments</h1>
          <button
            onClick={() => setShowModal(true)}
            className="w-full sm:w-auto bg-blue-600 text-white px-4 md:px-6 py-2 rounded-xl font-bold hover:bg-blue-700 transition"
          >
            + Add Payment
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveTab("transactions")}
            className={`px-4 py-2 rounded-xl font-bold text-sm border ${activeTab === "transactions" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
          >
            Transactions
          </button>
          <button
            onClick={() => setActiveTab("renewals")}
            className={`px-4 py-2 rounded-xl font-bold text-sm border ${activeTab === "renewals" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
          >
            Renewal Requests
            {renewalRequests.length > 0 && (
              <span className="ml-2 bg-red-600 text-white px-2 py-0.5 rounded-full text-xs">
                {renewalRequests.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("invoices")}
            className={`px-4 py-2 rounded-xl font-bold text-sm border ${activeTab === "invoices" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
          >
            Invoices
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {summaryCards.map((card, i) => (
                <div key={i} className={`bg-white p-5 rounded-2xl shadow-sm border-l-4 ${card.color}`}>
                  <div className="text-2xl mb-2">{card.icon}</div>
                  <p className="text-xs text-gray-400 font-bold uppercase">{card.label}</p>
                  <p className="text-2xl font-black text-gray-800 mt-1">{card.value}</p>
                </div>
              ))}
            </div>

            {activeTab === "transactions" && (
              <>
                {/* Transactions Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
                  <div className="p-4 md:p-6 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800">Recent Transactions</h2>
                  </div>
                  <table className="min-w-[900px] w-full">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="p-4 text-left">User</th>
                        <th className="p-4 text-left">Plan</th>
                        <th className="p-4 text-right">Amount</th>
                        <th className="p-4 text-center">Method</th>
                        <th className="p-4 text-center">Status</th>
                        <th className="p-4 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {transactions.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-gray-400">No transactions found</td>
                        </tr>
                      ) : (
                        transactions.map((tx) => (
                          <tr key={tx.id} className="hover:bg-gray-50">
                            <td className="p-4">
                              <p className="font-bold text-gray-800">{tx.user_name || "—"}</p>
                              <p className="text-xs text-gray-400">{tx.user_email || "—"}</p>
                            </td>
                            <td className="p-4 text-gray-600">{tx.plan_name || "—"}</td>
                            <td className="p-4 text-right font-bold text-gray-800">₹{tx.amount ?? "—"}</td>
                            <td className="p-4 text-center">
                              <span className="px-2 py-1 bg-gray-100 rounded text-xs font-bold text-gray-600">
                                {tx.payment_method}
                              </span>
                            </td>
                            <td className="p-4 text-center">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                tx.status === "COMPLETED" ? "bg-green-100 text-green-700" :
                                tx.status === "PENDING" ? "bg-yellow-100 text-yellow-700" :
                                "bg-red-100 text-red-700"
                              }`}>
                                {tx.status}
                              </span>
                            </td>
                            <td className="p-4 text-gray-500 text-sm">
                              {tx.created_at ? formatLocalDate(tx.created_at) : "—"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === "renewals" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
                <div className="p-4 md:p-6 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-800">Pending Renewal Requests</h2>
                  <button
                    onClick={fetchData}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-bold hover:bg-gray-200 transition"
                  >
                    Refresh
                  </button>
                </div>

                <table className="min-w-[1100px] w-full">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="p-4 text-left">User</th>
                      <th className="p-4 text-left">Plan</th>
                      <th className="p-4 text-center">Pref</th>
                      <th className="p-4 text-center">Duration</th>
                      <th className="p-4 text-left">Ref</th>
                      <th className="p-4 text-left">Ref Date</th>
                      <th className="p-4 text-right">Amount</th>
                      <th className="p-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {renewalRequests.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-gray-400">No pending requests</td>
                      </tr>
                    ) : (
                      renewalRequests.map((r) => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="p-4">
                            <p className="font-bold text-gray-800">{r.user_name || "—"}</p>
                            <p className="text-xs text-gray-400">{r.user_email || "—"}</p>
                            <p className="text-xs text-gray-400">WA: {r.user_phone || "—"}</p>
                          </td>
                          <td className="p-4 text-gray-600">{r.plan_name || "—"}</td>
                          <td className="p-4 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              String(r.payment_preference).toUpperCase() === "UPI" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"
                            }`}>
                              {String(r.payment_preference || "").toUpperCase()}
                            </span>
                          </td>
                          <td className="p-4 text-center text-gray-700">{(r.plan_duration_days || r.duration_days) ? `${r.plan_duration_days || r.duration_days} days` : "—"}</td>
                          <td className="p-4 text-gray-600">{r.payment_reference || "—"}</td>
                          <td className="p-4 text-gray-600">
                            {r.payment_reference_date ? formatLocalDate(r.payment_reference_date) : "—"}
                          </td>
                          <td className="p-4 text-right font-bold text-gray-800">₹{r.plan_price ?? "—"}</td>
                          <td className="p-4 text-center">
                            <div className="flex flex-col sm:flex-row gap-2 justify-center">
                              <button
                                onClick={() => approveRenewal(r.id, "UPI")}
                                className="px-3 py-2 rounded-xl font-bold text-xs bg-blue-600 text-white hover:bg-blue-700"
                              >
                                Approve UPI
                              </button>
                              <button
                                onClick={() => approveRenewal(r.id, "BANK")}
                                className="px-3 py-2 rounded-xl font-bold text-xs bg-green-600 text-white hover:bg-green-700"
                              >
                                Approve Bank
                              </button>
                              <button
                                onClick={() => rejectRenewal(r.id)}
                                className="px-3 py-2 rounded-xl font-bold text-xs bg-red-600 text-white hover:bg-red-700"
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "invoices" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
                <div className="p-4 md:p-6 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-800">Invoices</h2>
                  <button
                    onClick={fetchData}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-bold hover:bg-gray-200 transition"
                  >
                    Refresh
                  </button>
                </div>

                <table className="min-w-[1100px] w-full">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="p-4 text-left">Invoice</th>
                      <th className="p-4 text-left">Customer</th>
                      <th className="p-4 text-left">Plan</th>
                      <th className="p-4 text-center">Duration</th>
                      <th className="p-4 text-right">Amount</th>
                      <th className="p-4 text-center">Email</th>
                      <th className="p-4 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {invoices.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="p-8 text-center text-gray-400">No invoices</td>
                      </tr>
                    ) : (
                      invoices.map((inv) => (
                        <tr key={inv.id} className="hover:bg-gray-50">
                          <td className="p-4">
                            <p className="font-bold text-gray-800">{inv.invoice_number || `#${inv.id}`}</p>
                            <p className="text-xs text-gray-400">{inv.issued_at ? formatLocalDateTime(inv.issued_at) : "—"}</p>
                          </td>
                          <td className="p-4">
                            <p className="font-bold text-gray-800">{inv.customer_name || "—"}</p>
                            <p className="text-xs text-gray-400">{inv.customer_email || "—"}</p>
                            <p className="text-xs text-gray-400">WA: {inv.customer_phone || "—"}</p>
                          </td>
                          <td className="p-4 text-gray-700">{inv.plan_name || inv.plan_id || "—"}</td>
                          <td className="p-4 text-center text-gray-700">{inv.duration_days ? `${inv.duration_days} days` : "—"}</td>
                          <td className="p-4 text-right font-bold text-gray-800">₹{inv.amount ?? "—"}</td>
                          <td className="p-4 text-center">
                            {inv.whatsapp_sent ? (
                              <span className="px-2 py-1 rounded text-xs font-bold bg-green-100 text-green-700">SENT</span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-bold bg-red-100 text-red-700" title={inv.whatsapp_error || ""}>
                                NOT SENT
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            <button
                              onClick={() => resendInvoice(inv.id)}
                              className="px-3 py-2 rounded-xl font-bold text-xs bg-blue-600 text-white hover:bg-blue-700"
                            >
                              Resend Email
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* User Subscriptions (shows even if there are no transactions yet) */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto mt-8">
              <div className="p-4 md:p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-800">User Subscriptions</h2>
              </div>
              <table className="min-w-[900px] w-full">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="p-4 text-left">User</th>
                    <th className="p-4 text-left">Plan</th>
                    <th className="p-4 text-center">Status</th>
                    <th className="p-4 text-left">Expiry</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="p-8 text-center text-gray-400">No users found</td>
                    </tr>
                  ) : (
                    users.map((u) => {
                      const plan = u.plan_id ? planById[String(u.plan_id)] : null;
                      const expiry = u.plan_expiry_date ? parseServerDate(u.plan_expiry_date) : null;
                      const expiryLabel = expiry ? formatLocalDate(expiry) : "—";
                      const isActive = String(u.status || "").toUpperCase() === "ACTIVE";
                      const isExpired = expiry ? expiry.getTime() < Date.now() : false;

                      return (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="p-4">
                            <p className="font-bold text-gray-800">{u.name || "—"}</p>
                            <p className="text-xs text-gray-400">{u.email || "—"}</p>
                          </td>
                          <td className="p-4 text-gray-600">{plan?.name || "No Plan"}</td>
                          <td className="p-4 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              !isActive ? "bg-red-100 text-red-700" :
                              isExpired ? "bg-yellow-100 text-yellow-700" :
                              "bg-green-100 text-green-700"
                            }`}>
                              {!isActive ? "DISABLED" : isExpired ? "EXPIRED" : "ACTIVE"}
                            </span>
                          </td>
                          <td className="p-4 text-gray-500 text-sm">{expiryLabel}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Add Transaction Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white p-8 rounded-2xl w-full max-w-md shadow-2xl">
              <h3 className="text-2xl font-bold mb-6 text-gray-800">Record Payment</h3>
              <form onSubmit={handleAddTransaction} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">SELECT USER</label>
                  <select
                    className="w-full border p-3 rounded-xl mt-1"
                    required
                    value={newTx.user_id}
                    onChange={(e) => setNewTx({ ...newTx, user_id: e.target.value })}
                  >
                    <option value="">Choose user...</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">SELECT PLAN</label>
                  <select
                    className="w-full border p-3 rounded-xl mt-1"
                    value={newTx.plan_id}
                    onChange={(e) => {
                      const plan = plans.find(p => p.id === parseInt(e.target.value));
                      setNewTx({ ...newTx, plan_id: e.target.value, amount: plan?.price || "" });
                    }}
                  >
                    <option value="">Choose plan...</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} (₹{p.price})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">AMOUNT (₹)</label>
                  <input
                    type="number"
                    className="w-full border p-3 rounded-xl mt-1"
                    required
                    value={newTx.amount}
                    onChange={(e) => setNewTx({ ...newTx, amount: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-500 ml-1">METHOD</label>
                    <select
                      className="w-full border p-3 rounded-xl mt-1"
                      value={newTx.payment_method}
                      onChange={(e) => setNewTx({ ...newTx, payment_method: e.target.value })}
                    >
                      <option value="UPI">UPI</option>
                      <option value="BANK">Bank Transfer</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 ml-1">STATUS</label>
                    <select
                      className="w-full border p-3 rounded-xl mt-1"
                      value={newTx.status}
                      onChange={(e) => setNewTx({ ...newTx, status: e.target.value })}
                    >
                      <option value="COMPLETED">Completed</option>
                      <option value="PENDING">Pending</option>
                      <option value="FAILED">Failed</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">NOTES</label>
                  <input
                    className="w-full border p-3 rounded-xl mt-1"
                    placeholder="Optional notes..."
                    value={newTx.notes}
                    onChange={(e) => setNewTx({ ...newTx, notes: e.target.value })}
                  />
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-100 py-3 rounded-xl font-bold text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold"
                  >
                    Save Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
